import { useContext, useEffect, useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
  Theme,
} from '@material-ui/core';
import { AppTextField } from '@vyce/core/src/components/inputs/AppTextField';

import { MAIN_CONTAINER_ID } from '../../constants';
import { DeviceContext } from '../../contexts';
import { ButtonTitleWithLoading } from '../ButtonTitleWithLoading';
import { ChatData } from '../../types';
import { AppSearchInput } from '../inputs/AppSearchInput';
import { AppCheckbox } from '../inputs/AppCheckbox';
import ChatAvatar from './ChatAvatar';
import { AppChip } from '../AppChip';

export const ChatForwardMessageDialog = ({
  open,
  handleClose,
  chats,
  forwardMessage,
}: {
  open: boolean;
  handleClose: () => void;
  chats: ChatData[];
  forwardMessage: (recipients: ChatData[], message: string) => Promise<void>;
}) => {
  const theme = useTheme<Theme>();
  const { isMobile } = useContext(DeviceContext);
  const [message, setMessage] = useState<string>('');
  const [searchItems, setSearchItems] = useState<ChatData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedChats, setSelectedChats] = useState<ChatData[]>([]);

  const handleSubmit = async () => {
    setLoading(true);
    await forwardMessage(selectedChats, message);
    setSelectedChats([]);
    setMessage('');
    setLoading(false);
    handleClose();
  };

  const onChatSelect = (chat: ChatData) => {
    if (selectedChats.some(selectedChat => selectedChat.id === chat.id)) {
      setSelectedChats(prev => prev.filter(selectedChat => selectedChat.id !== chat.id));
    } else {
      setSelectedChats(prev => [...prev, chat]);
    }
  };

  const onSearch = async (search: string) => {
    setSearchItems(chats.filter(it => it.name?.toLowerCase().includes(search.toLowerCase())));
  };

  useEffect(() => {
    if (open) {
      onSearch('');
      setSearchItems(chats);
    }
  }, [open]);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={isMobile}
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>Forward message</DialogTitle>

      <DialogContent style={{ width: '100%' }}>
        <AppTextField
          label="Add a message (optional)"
          fullWidth
          value={message}
          onChange={e => setMessage(e.target.value)}
        />

        <Typography variant="body1" style={{ marginTop: 16, marginBottom: 8 }}>
          Select recipients:
        </Typography>

        <Box display="flex" flexDirection="row" flexWrap="wrap" gridGap={4} marginBottom={2}>
          {selectedChats.map(chat => (
            <AppChip key={chat.id} label={chat.name || ''} onDelete={() => onChatSelect(chat)} />
          ))}
        </Box>

        <AppSearchInput
          flex={1}
          onChange={e => onSearch(e.target.value)}
          isSmall
          smallRounding
          isBorder
          expanded={true}
        />

        <Box height={400} marginTop={2} overflow="auto" display="flex" flexDirection="column">
          {searchItems.map(chat => (
            <Box
              display="flex"
              alignItems="center"
              key={chat.id}
              gridGap={4}
              onClick={() => onChatSelect(chat)}
              style={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                paddingBottom: 8,
                paddingTop: 8,
                cursor: 'pointer',
              }}
            >
              <AppCheckbox
                variant="rectangle"
                checked={selectedChats.some(selectedChat => selectedChat.id === chat.id)}
              />
              <ChatAvatar size={28} avatarUrl={chat.avatarUrl} name={chat.name || ''} />

              <Typography style={{ marginLeft: 8 }} variant="caption">
                {chat.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>

        <Button
          style={{ width: 160 }}
          disabled={loading}
          onClick={handleSubmit}
          color="primary"
          variant="contained"
        >
          <ButtonTitleWithLoading title="Forward message" loaderVariant="primary" loading={loading} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
